import { useState, type FormEvent, type ReactNode } from "react";

import type { AdminAuditLogItem } from "@chaochud/contracts";

/** Critical-action confirmation (docs/roadmap.md §13.4). */
export function CriticalActionForm({
  title,
  targetLabel,
  currentState,
  expectedState,
  impact,
  submitLabel,
  onSubmit,
}: {
  title: string;
  targetLabel: string;
  currentState: string;
  expectedState: string;
  impact: string;
  submitLabel: string;
  onSubmit: (payload: {
    reasonCode: string;
    reasonText?: string;
    internalNote?: string;
  }) => Promise<void>;
}) {
  const [reasonCode, setReasonCode] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await onSubmit({
        reasonCode,
        reasonText: reasonText || undefined,
        internalNote: internalNote || undefined,
      });
      setReasonCode("");
      setReasonText("");
      setInternalNote("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-warning-bg bg-warning-bg/50 p-4">
      <h3 className="text-sm font-semibold text-warning-text">{title}</h3>
      <dl className="grid gap-1 text-xs text-warning-text">
        <div>
          <dt className="inline font-medium">Target: </dt>
          <dd className="inline">{targetLabel}</dd>
        </div>
        <div>
          <dt className="inline font-medium">Current → Expected: </dt>
          <dd className="inline">
            {currentState} → {expectedState}
          </dd>
        </div>
        <div>
          <dt className="inline font-medium">Impact: </dt>
          <dd className="inline">{impact}</dd>
        </div>
      </dl>
      <label className="block text-sm text-ink">
        Reason code *
        <input
          required
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-line px-3 py-2 text-sm"
          placeholder="POLICY_VIOLATION"
        />
      </label>
      <label className="block text-sm text-ink">
        Reason text
        <textarea
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-line px-3 py-2 text-sm"
          rows={2}
        />
      </label>
      <label className="block text-sm text-ink">
        Internal note
        <textarea
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-line px-3 py-2 text-sm"
          rows={2}
        />
      </label>
      {error ? <p className="text-sm text-error-text">{error}</p> : null}
      <button
        type="submit"
        disabled={pending}
        className="rounded-lg bg-plum px-3.5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
      >
        {submitLabel}
      </button>
    </form>
  );
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      {title && <h1 className="font-heading text-lg font-semibold text-ink">{title}</h1>}
      {children}
    </div>
  );
}

export function StatusPill({ children, tone = "neutral" }: { children: ReactNode; tone?: "success" | "warning" | "error" | "neutral" }) {
  const toneClass =
    tone === "success"
      ? "bg-success-bg text-success-text"
      : tone === "warning"
        ? "bg-warning-bg text-warning-text"
        : tone === "error"
          ? "bg-error-bg text-error-text"
          : "bg-neutral-bg text-neutral-text";
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${toneClass}`}>{children}</span>;
}

export function FilterTabs({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-full px-3.5 py-2 text-[12.5px] font-semibold ${
            value === option.value ? "border border-plum bg-plum text-white" : "border border-slate-line bg-white text-ink"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder ?? "ค้นหา..."}
      className="w-[220px] rounded-lg border border-slate-line px-3.5 py-2 text-[13px]"
    />
  );
}

export interface AuditTrailEntry {
  id: string;
  action: string;
  actorLabel: string;
  createdAt: string;
  reasonText?: string | null;
  internalNote?: string | null;
}

/** Extracts the reasonText/internalNote every critical action already writes into AuditLog.metadata (see admin-common.dto.ts). */
export function toAuditTrailEntry(item: AdminAuditLogItem): AuditTrailEntry {
  const metadata = (item.metadata ?? {}) as { reasonText?: unknown; internalNote?: unknown };
  return {
    id: item.id,
    action: item.action,
    actorLabel: item.actorUserId ?? item.actorType,
    createdAt: item.createdAt,
    reasonText: typeof metadata.reasonText === "string" ? metadata.reasonText : null,
    internalNote: typeof metadata.internalNote === "string" ? metadata.internalNote : null,
  };
}

/** Per-entity recent-activity feed (AdminDetail.dc.html), backed by the real admin/audit-logs endpoint filtered by resourceType+resourceId. */
export function AuditTrail({ entries, isLoading }: { entries: AuditTrailEntry[]; isLoading?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-line bg-slate-surface p-4.5">
      <p className="mb-3 text-[13px] font-semibold text-ink">ประวัติการดำเนินการ</p>
      {isLoading ? (
        <p className="text-[12.5px] text-muted">กำลังโหลด...</p>
      ) : entries.length === 0 ? (
        <p className="text-[12.5px] text-muted">ยังไม่มีประวัติ</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {entries.map((entry) => (
            <li key={entry.id} className="text-[12.5px]">
              <span className="font-semibold text-ink">{entry.action}</span>
              <span className="text-muted"> · {entry.actorLabel}</span>
              <p className="text-[11.5px] text-muted">{new Date(entry.createdAt).toLocaleString("th-TH")}</p>
              {entry.reasonText && <p className="mt-0.5 text-[11.5px] text-ink">เหตุผล: {entry.reasonText}</p>}
              {entry.internalNote && (
                <p className="mt-0.5 rounded bg-slate-row px-2 py-1 text-[11.5px] text-ink">
                  บันทึกภายใน: {entry.internalNote}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface DataTablePagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export function DataTable({
  headers,
  rows,
  pagination,
  onPageChange,
}: {
  headers: string[];
  rows: Array<Array<ReactNode>>;
  /** Pass together with `onPageChange` to render a count + prev/next footer (backed by the API's already-existing `pagination` metadata). */
  pagination?: DataTablePagination;
  onPageChange?: (page: number) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-line bg-slate-surface">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-line bg-slate-header text-[11.5px] font-bold text-muted">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-4.5 py-3">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-b border-slate-row last:border-0">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-4.5 py-3.5 align-top text-ink">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={headers.length} className="px-4.5 py-6 text-center text-[12.5px] text-muted">
                  ไม่มีรายการ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && onPageChange && <DataTablePaginationFooter pagination={pagination} onPageChange={onPageChange} />}
    </div>
  );
}

function DataTablePaginationFooter({
  pagination,
  onPageChange,
}: {
  pagination: DataTablePagination;
  onPageChange: (page: number) => void;
}) {
  const { page, pageSize, totalItems, totalPages } = pagination;
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(totalItems, page * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-line px-4.5 py-3 text-[12.5px]">
      <span className="text-muted">
        {totalItems === 0 ? "ไม่มีรายการ" : `แสดง ${from}–${to} จาก ${totalItems.toLocaleString("th-TH")} รายการ`}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="rounded-lg border border-slate-line px-3 py-1.5 font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          ก่อนหน้า
        </button>
        <span className="font-semibold text-ink">
          หน้า {page} / {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="rounded-lg border border-slate-line px-3 py-1.5 font-semibold text-ink disabled:cursor-not-allowed disabled:opacity-40"
        >
          ถัดไป
        </button>
      </div>
    </div>
  );
}
